import { getAccessToken } from '../auth/msal';
import { apiConfig } from '../config/authConfig';
import type {
  AddOrderRequest,
  ApiResponse,
  CreateRunRequest,
  Receipt,
  Run,
  RunSummary,
} from '../types';

// Coffee Run API Client - Enhanced Implementation
class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  // Receipt operations
  async uploadReceipt(runId: string, file: File): Promise<Receipt> {
    const formData = new FormData();
    formData.append('receipt', file);

    const response = await fetch(`${this.baseUrl}/runs/${runId}/receipt`, {
      method: 'POST',
      headers: await this.getHeaders(true, false), // Don't include Content-Type for FormData
      body: formData,
    });

    return this.handleResponse<Receipt>(response);
  }

  async getReceipt(runId: string): Promise<Receipt> {
    const response = await fetch(`${this.baseUrl}/runs/${runId}/receipt`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    return this.handleResponse<Receipt>(response);
  }

  // Safe call wrapper for receipt operations
  async safeUploadReceipt(runId: string, file: File) {
    return this.safeCall(() => this.uploadReceipt(runId, file));
  }

  async safeGetReceipt(runId: string) {
    return this.safeCall(() => this.getReceipt(runId));
  }

  private async getHeaders(
    includeAuth = true,
    includeContentType = true
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (includeAuth) {
      try {
        const token = await getAccessToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.warn('Failed to get access token:', error);
      }
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage =
          errorJson.error || `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = errorText || `HTTP error! status: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text);
    } catch {
      return text as T;
    }
  }

  async get<T>(endpoint: string, includeAuth = true): Promise<T> {
    const headers = await this.getHeaders(includeAuth);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers,
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data: any, includeAuth = true): Promise<T> {
    const headers = await this.getHeaders(includeAuth);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(endpoint: string, data: any, includeAuth = true): Promise<T> {
    const headers = await this.getHeaders(includeAuth);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async delete(endpoint: string, includeAuth = true): Promise<void> {
    const headers = await this.getHeaders(includeAuth);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers,
    });

    await this.handleResponse<void>(response);
  }

  // Health check endpoints
  async ping(): Promise<{
    status: string;
    timestamp: string;
    version: string;
  }> {
    return this.get('/ping', false);
  }

  async securePing(): Promise<{ status: string; timestamp: string }> {
    return this.get('/secure-ping', true);
  }

  // Run management endpoints
  async getRuns(createdBy?: string): Promise<Run[]> {
    const queryParam = createdBy
      ? `?createdBy=${encodeURIComponent(createdBy)}`
      : '';
    const response = await this.get<any>(`/runs${queryParam}`, false);
    
    // Handle both array response (direct) and object response (wrapped)
    if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.runs)) {
      return response.runs;
    }
    
    // Fallback to empty array
    return [];
  }

  async getRun(id: string): Promise<Run> {
    return this.get(`/runs/${id}`, false);
  }

  async getRunByShareCode(shareCode: string): Promise<Run> {
    return this.get(`/runs/share/${shareCode}`, false);
  }

  async createRun(run: CreateRunRequest): Promise<Run> {
    // Updated endpoint to avoid route conflict with GET /runs
    return this.post('/runs/create', run, false);
  }

  async getRunSummary(id: string): Promise<RunSummary> {
    return this.get(`/runs/${id}/summary`, false);
  }

  // Order management endpoints
  async addOrder(runId: string, order: AddOrderRequest): Promise<Run> {
    return this.post(`/runs/${runId}/orders`, order, false);
  }

  // Utility methods
  async checkConnection(): Promise<boolean> {
    try {
      await this.ping();
      return true;
    } catch {
      return false;
    }
  }

  // Error handling wrapper for UI components
  async safeCall<T>(
    operation: () => Promise<T>,
    defaultValue?: T
  ): Promise<ApiResponse<T>> {
    try {
      const data = await operation();
      return { data };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('API call failed:', errorMessage);
      return {
        error: errorMessage,
        data: defaultValue,
      };
    }
  }

  // =====================================================================
  // DELEGATION AND HISTORY METHODS (Prompt 5)
  // =====================================================================

  // Delegation operations
  async addDelegate(
    runId: string,
    delegateOid: string
  ): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/runs/${runId}/delegate`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ delegateOid }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add delegate: ${response.statusText}`);
    }

    return response.json();
  }

  async searchGraphUsers(
    query: string,
    limit = 10
  ): Promise<import('../types').GraphUser[]> {
    const response = await fetch(
      `${this.baseUrl}/graph/users?query=${encodeURIComponent(query)}&limit=${limit}`,
      {
        method: 'GET',
        headers: await this.getHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to search users: ${response.statusText}`);
    }

    return response.json();
  }

  // History operations
  async searchPersons(
    query: string,
    limit = 10
  ): Promise<import('../types').PersonSearchResult[]> {
    const response = await fetch(
      `${this.baseUrl}/history/persons?query=${encodeURIComponent(query)}&limit=${limit}`,
      {
        method: 'GET',
        headers: await this.getHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to search persons: ${response.statusText}`);
    }

    return response.json();
  }

  async getUsuals(
    personKey: string,
    vendor?: string,
    runType?: string,
    limit = 10
  ): Promise<import('../types').UsualSuggestion[]> {
    const params = new URLSearchParams({
      personKey,
      limit: limit.toString(),
    });

    if (vendor) params.append('vendor', vendor);
    if (runType) params.append('type', runType);

    const response = await fetch(`${this.baseUrl}/history/usuals?${params}`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get usuals: ${response.statusText}`);
    }

    return response.json();
  }

  async getRunHistory(
    ownerId?: string,
    limit = 50,
    offset = 0
  ): Promise<import('../types').HistoryResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (ownerId) params.append('ownerId', ownerId);

    const response = await fetch(`${this.baseUrl}/runs?${params}`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get run history: ${response.statusText}`);
    }

    return response.json();
  }

  // Safe wrapper methods for new endpoints
  async safeAddDelegate(
    runId: string,
    delegateOid: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.safeCall(() => this.addDelegate(runId, delegateOid));
  }

  async safeSearchPersons(
    query: string,
    limit?: number
  ): Promise<ApiResponse<import('../types').PersonSearchResult[]>> {
    return this.safeCall(() => this.searchPersons(query, limit), []);
  }

  async safeGetUsuals(
    personKey: string,
    vendor?: string,
    runType?: string,
    limit?: number
  ): Promise<ApiResponse<import('../types').UsualSuggestion[]>> {
    return this.safeCall(
      () => this.getUsuals(personKey, vendor, runType, limit),
      []
    );
  }

  async safeGetRunHistory(
    ownerId?: string,
    limit?: number,
    offset?: number
  ): Promise<ApiResponse<import('../types').HistoryResponse>> {
    return this.safeCall(() => this.getRunHistory(ownerId, limit, offset), {
      runs: [],
      total: 0,
      offset: 0,
      limit: 0,
    });
  }
}

export const api = new ApiClient();
